import { createElement } from 'react';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import html2canvas from 'html2canvas';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { ProvidedJobPostPrintView } from '../components/ProvidedJobPostPrintView';
import type { PdfData } from '../components/ProvidedJobPostPrintView';
import type { AgentApiProvidedJobPostsPdfIndex } from '@/types/serializers';

const MARGIN_PX = 24 * 0.9;

/** html2canvas に渡すスケール。文字のジャギ低減用 */
const CANVAS_SCALE = 2;

/** 改ページ判定で「分割不可」とみなす行要素を識別するマーカー */
const PDF_ROW_SELECTOR = '[data-pdf-row="true"]';

/** Canvas 上の行境界（top と bottom はスケール適用後の Y 座標） */
interface RowBoundary {
  top: number;
  bottom: number;
}

/** ファイル名に使用できない文字を置換する */
const sanitizeFileName = (name: string): string => name.replace(/[/\\:*?"<>|]/g, '／');

/** 一括PDFの結果。削除済み等で取得できなかった件数を呼び出し側へ伝える (Issue #3426) */
export interface BulkPdfResult {
  requestedCount: number;
  downloadedCount: number;
  missingCount: number;
}

export default function usePdf() {
  const downloadPdf = async (id: number) => {
    const [pdfData] = await fetchPdfDataList([id]);
    if (!pdfData) throw new Error('求人票の取得に失敗しました');
    const blob = await generatePdfFromHtml(pdfData);

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizeFileName(pdfData.companyName)}_${sanitizeFileName(pdfData.title)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadMultiplePdfs = async (
    ids: number[],
    options?: { onProgress?: (current: number, total: number) => void }
  ): Promise<BulkPdfResult> => {
    if (ids.length === 0) return { requestedCount: 0, downloadedCount: 0, missingCount: 0 };

    const pdfDataList = await fetchPdfDataList(ids);
    if (pdfDataList.length === 0) throw new Error('求人票の取得に失敗しました');

    const zip = new JSZip();
    // 同名求人（会社名_タイトルが同一）がZIP内で上書きされないよう、重複時は連番を付ける
    const usedNameCounts = new Map<string, number>();
    for (const [index, pdfData] of pdfDataList.entries()) {
      const blob = await generatePdfFromHtml(pdfData);
      const baseName = sanitizeFileName(`${pdfData.companyName}_${pdfData.title}`);
      const count = (usedNameCounts.get(baseName) ?? 0) + 1;
      usedNameCounts.set(baseName, count);
      const fileName = count === 1 ? baseName : `${baseName}_${count}`;
      zip.file(`${fileName}.pdf`, blob);
      options?.onProgress?.(index + 1, pdfDataList.length);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const date = new Date().toISOString().split('T')[0];
    const a = document.createElement('a');
    a.href = url;
    a.download = `求人票_${date}_${pdfDataList.length}件.zip`;
    a.click();
    URL.revokeObjectURL(url);

    return {
      requestedCount: ids.length,
      downloadedCount: pdfDataList.length,
      missingCount: ids.length - pdfDataList.length,
    };
  };

  return { downloadPdf, downloadMultiplePdfs };
}

/** PDF描画に必要な項目だけを一括で取得する (Issue #3426) */
const fetchPdfDataList = async (ids: number[]): Promise<PdfData[]> => {
  const query = ids.map((id) => `ids[]=${id}`).join('&');
  const res = await fetch(`/agent/api/provided_job_posts/pdf_data.json?${query}`);
  if (!res.ok) {
    throw new Error(`求人票の取得に失敗しました (status: ${res.status})`);
  }
  const data: AgentApiProvidedJobPostsPdfIndex = await res.json();

  const byId = new Map(
    data.records.map((record) => {
      const companyInfo = record.company_information;

      return [
        record.id,
        {
          title: record.title,
          companyName: record.company_name,
          companyInfo: {
            industry: companyInfo?.industry || '----',
            address: companyInfo?.address || '----',
            url: companyInfo?.official_url || '----',
            establishedDate: companyInfo?.established_date || '----',
            listedPhase: companyInfo?.listed_phase || '----',
            presidentName: companyInfo?.president_name || '----',
            numberOfEmployees: companyInfo?.number_of_employees?.toString() || '----',
            description: companyInfo?.description || '----',
          },
          jobContent: record.job_content_json,
        },
      ];
    })
  );

  // レスポンスはID昇順で返るため、要求順に並べ直す。解決できなかったIDはここで落ちる
  return ids
    .map((id) => byId.get(id))
    .filter((pdfData): pdfData is PdfData => pdfData !== undefined);
};

/** オフスクリーンでReactコンポーネントをレンダリングし、html2canvasでキャプチャする。
 * 同時に各「分割不可な行」の Canvas 上の Y 座標も収集して返し、ページ分割で行境界スナップに使う。
 */
const renderToCanvas = async (
  pdfData: PdfData
): Promise<{ canvas: HTMLCanvasElement; rowBoundaries: RowBoundary[] }> => {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);

  const root = createRoot(container);
  try {
    flushSync(() => {
      root.render(createElement(ProvidedJobPostPrintView, { data: pdfData }));
    });

    const printRoot = container.firstElementChild as HTMLElement;

    // html2canvas でラスタライズする前に行境界を採取する（キャプチャ後は DOM 計測が当てにならない）。
    // CSS の pageBreakInside は html2canvas には効かないため、自前で行境界を取得して
    // addCanvasPagesToPdf 側でページ末尾をその行の手前まで巻き戻すロジックに使う。
    const rootRect = printRoot.getBoundingClientRect();
    const rowBoundaries: RowBoundary[] = Array.from(
      printRoot.querySelectorAll<HTMLElement>(PDF_ROW_SELECTOR)
    )
      .map((el) => {
        const rect = el.getBoundingClientRect();
        return {
          top: (rect.top - rootRect.top) * CANVAS_SCALE,
          bottom: (rect.bottom - rootRect.top) * CANVAS_SCALE,
        };
      })
      .sort((a, b) => a.top - b.top);

    // scale で約4倍解像度のキャンバスを生成し、文字のジャギを大幅に低減する。
    // PDF へは PNG で埋め込むため、スケールアップによる劣化はほぼ無い。
    const canvas = await html2canvas(printRoot, {
      scale: CANVAS_SCALE,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    return { canvas, rowBoundaries };
  } finally {
    root.unmount();
    document.body.removeChild(container);
  }
};

/** ページ末尾の slice 位置を行境界にスナップさせる (Issue #2649)。
 * 機械スライスのままだと canvas を Y 軸で機械的に切るため、テーブル行の途中で
 * 切断され文字が上下に分かれて潰れる。pageBottom をまたぐ行が見つかれば、
 * その行を次ページ先頭に送るよう sliceHeight を巻き戻す。
 *
 * - 最終ページでは巻き戻し不要（残り全部このページで使う）
 * - 行が canvas の高さを超える/ページ高を超える場合は巻き戻せないので機械スライスにフォールバック
 *   （`straddling.top <= currentY` のとき = 既にページ先頭から始まっている長い行）
 */
const snapSliceToRowBoundary = (
  currentY: number,
  defaultSliceHeight: number,
  canvasHeight: number,
  rowBoundaries: RowBoundary[]
): number => {
  const pageBottom = currentY + defaultSliceHeight;
  if (pageBottom >= canvasHeight) return defaultSliceHeight;

  const straddling = rowBoundaries.find((r) => r.top < pageBottom && r.bottom > pageBottom);
  if (!straddling) return defaultSliceHeight;
  if (straddling.top <= currentY) return defaultSliceHeight;

  return straddling.top - currentY;
};

/** Canvasの内容をページ分割してjsPDFに追加する */
const addCanvasPagesToPdf = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  rowBoundaries: RowBoundary[],
  addPageBeforeFirst: boolean
): void => {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const pxPerMm = canvas.width / pdfWidth;
  const pageHeightPx = pdfHeight * pxPerMm;
  const contentHeightPx = pageHeightPx - MARGIN_PX * 2;

  let currentY = 0;
  let pageNumber = 0;

  while (currentY < canvas.height) {
    if (addPageBeforeFirst || pageNumber > 0) {
      pdf.addPage();
    }

    const remainingHeight = canvas.height - currentY;
    const defaultSliceHeight = Math.min(contentHeightPx, remainingHeight);
    const sliceHeight = snapSliceToRowBoundary(
      currentY,
      defaultSliceHeight,
      canvas.height,
      rowBoundaries
    );

    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = MARGIN_PX * 2 + sliceHeight;

    const ctx = pageCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

      ctx.drawImage(
        canvas,
        0,
        currentY,
        canvas.width,
        sliceHeight,
        0,
        MARGIN_PX,
        canvas.width,
        sliceHeight
      );
    }

    // 文字を含むため可逆圧縮の PNG を使用。JPEG だとブロックノイズで文字エッジが滲む。
    const pageImgData = pageCanvas.toDataURL('image/png');
    const pageImgHeight = (pageCanvas.height * pdfWidth) / pageCanvas.width;
    pdf.addImage(pageImgData, 'PNG', 0, 0, pdfWidth, pageImgHeight, undefined, 'FAST');

    currentY += sliceHeight;
    pageNumber++;
  }
};

const generatePdfFromHtml = async (pdfData: PdfData): Promise<Blob> => {
  const { canvas, rowBoundaries } = await renderToCanvas(pdfData);

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  addCanvasPagesToPdf(pdf, canvas, rowBoundaries, false);

  return pdf.output('blob');
};
